import logger from '../../utils/logger';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiClient';
import { 
    DATASETS_URL, 
    DEFAULT_PAGE_LIMIT, 
    UPLOADS_URL, 
    UPLOAD_START_URL, 
    UPLOAD_COMPLETE_URL, 
    UPLOAD_BATCH_COMPLETE_URL,
    DATASET_COMPLETE_URL,
    DATASET_ABORT_URL
} from '../../constants/apiEndpoints';

export { formatFileSize } from '../../utils/formatUtils';
// Helper to split file into chunks
export const splitFileIntoChunks = (file, chunkSize) => {
    const chunks = [];
    let offset = 0;
    while (offset < file.size) {
        const end = Math.min(offset + chunkSize, file.size);
        chunks.push(file.slice(offset, end));
        offset = end;
    }
    return chunks;
};

// Async thunks

// Phase 1: Initialize upload and get configuration (chunk_size, algorithm, upload_id)
// This endpoint does NOT return pre-signed URLs
export const initUploadChecksum = createAsyncThunk(
    'upload/initUploadChecksum',
    async (uploadData, { rejectWithValue }) => {
        try {
            const {
                dataset_name,
                contract_name,
                business_unit,
                project,
                data_provider_name,
                files,
                _signal
            } = uploadData;

               return await apiFetch(UPLOADS_URL, {
                method: 'POST',
                body: JSON.stringify({
                    dataset_name: dataset_name || '',
                    contract_name: contract_name || '',
                    business_unit: business_unit || '',
                    project: project || '',
                    data_provider_name: data_provider_name || '',
                    files: files.map(file => ({
                        file_name: file.file_name,
                        extension: file.extension || '',
                        file_size: file.file_size,
                        file_path: file.file_path,
                        content_type: file.content_type || 'application/octet-stream'
                    }))
                }),
                signal: _signal
            });
        } catch (error) {
            if (error.name === 'AbortError') {
                logger.debug('Init upload checksum request was aborted');
                return rejectWithValue('Request aborted');
            }
            return rejectWithValue(error.message);
        }
    }
);

// Phase 2: Submit checksums and get pre-signed URLs
// Batch upload thunk for multiple files
export const startBatchUpload = createAsyncThunk(
    'upload/startBatchUpload',
    async (uploadData, { rejectWithValue }) => {
        try {
            const {
                business_unit,
                project,
                data_provider_name,
                files,
                _signal // PATCH: allow passing AbortSignal
            } = uploadData;

            return await apiFetch(UPLOAD_START_URL, {
                method: 'POST',
                body: JSON.stringify({
                    dataset_name: uploadData.dataset_name || '',
                    contract_name: uploadData.contract_name || '',
                    business_unit: business_unit || '',
                    project: project || '',
                    data_provider_name: data_provider_name || '',
                    files: files.map(file => {
                        const filePayload = {
                            file_name: file.file_name,
                            extension: file.extension || '',
                            file_size: file.file_size,
                            file_path: file.file_path,
                            content_type: file.content_type || 'application/octet-stream'
                        };
                        
                        // Phase 2: Include upload_id and parts if present (checksum workflow)
                        if (file.upload_id) {
                            filePayload.upload_id = file.upload_id;
                        }
                        if (file.parts) {
                            filePayload.parts = file.parts;
                        }
                        
                        return filePayload;
                    })
                }),
                signal: _signal
            });
        } catch (error) {
            // PATCH: handle abort error message
            if (error.name === 'AbortError') {
                return rejectWithValue('Upload initialization aborted');
            }
            return rejectWithValue(error.message);
        }
    }
);

export const completeUpload = createAsyncThunk(
    'upload/completeUpload',
    async (uploadData, { rejectWithValue }) => {
        try {
            const { key, upload_id, etags } = uploadData;
            return await apiFetch(UPLOAD_COMPLETE_URL(upload_id), {
                method: 'POST',
                body: JSON.stringify({ key, etags })
            });
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Batch complete: complete multiple file uploads in a single API call
export const batchCompleteUpload = createAsyncThunk(
    'upload/batchCompleteUpload',
    async ({ uploads }, { rejectWithValue }) => {
        try {
            return await apiFetch(UPLOAD_BATCH_COMPLETE_URL, {
                method: 'POST',
                body: JSON.stringify({ uploads })
            });
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const completeDatasetAPI = createAsyncThunk(
    'upload/completeDataset',
    async ({ datasetId, status, businessUnit, project, contractId, dataProviderName, contractName, datasetName }, { rejectWithValue }) => {
        try {
            return await apiFetch(DATASET_COMPLETE_URL(datasetId), {
                method: 'POST',
                body: JSON.stringify({
                    Status: status,
                    business_unit: businessUnit || '',
                    project: project || '',
                    contract_name: contractName || '',
                    dataset_name: datasetName || '',
                    data_provider_name: dataProviderName || ''
                })
            });
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const abortUploadAPI = createAsyncThunk(
    'upload/abortUpload',
    async (uploadData, { rejectWithValue }) => {
        try {
            const { dataset_id, cancel_reason = 'User cancelled upload' } = uploadData;
            const url = `${DATASET_ABORT_URL(dataset_id)}?cancel_reason=${encodeURIComponent(cancel_reason)}`;
            return await apiFetch(url, { method: 'POST' });
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);
/***
 * The backend needs to know which data provider's datasets to return. Without it, the API doesn't know whose uploads to show
 */
export const fetchUploadedFiles = createAsyncThunk(
    'upload/fetchUploadedFiles',
    async ({ limit = DEFAULT_PAGE_LIMIT, offset = 0, contractId, search = '' }, { rejectWithValue }) => {
        try {
            const url = DATASETS_URL(contractId, limit, offset, search);
            return await apiFetch(url, { method: 'GET' });
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);
export const uploadChunk = createAsyncThunk(
    'upload/uploadChunk',
    async ({ url, chunk, checksum = null, algorithm = null, requiresChecksums = false, contentType = null, kmsHeaders = null, signal = null }, { rejectWithValue }) => {
        try {
            const headers = {};
            
            // CRITICAL: When using pre-signed URLs with checksums, AWS expects specific headers
            // The pre-signed URL signature includes content-type in SignedHeaders
            // We must send the EXACT content-type that backend used when generating signature
            // Backend provides this in the 'fields' object of the response
            
            // Merge KMS headers first (contains Content-Type + KMS encryption headers)
            // KMS headers are CRITICAL for direct uploads - presigned URL signature includes them
            if (kmsHeaders && Object.keys(kmsHeaders).length > 0) {
                Object.assign(headers, kmsHeaders);
            }
            
            // Override Content-Type if explicitly provided (backward compatibility)
            if (contentType) {
                headers['Content-Type'] = contentType;
            }
            
            // requiresChecksums flag controls whether to attach checksum headers
            // - true: Attach checksum headers (backend requires and validates them)
            // - false or undefined: Don't attach checksum headers (backward compatibility)
            if (requiresChecksums === true && checksum && algorithm) {
                // Import getChecksumHeaderName function inline to avoid circular dependency
                const CHECKSUM_HEADERS = {
                    'SHA256': 'x-amz-checksum-sha256',
                    'SHA1': 'x-amz-checksum-sha1',
                    'CRC32': 'x-amz-checksum-crc32',
                    'CRC32C': 'x-amz-checksum-crc32c'
                };
                const normalizedAlgo = (algorithm || '').trim().toUpperCase();
                const headerName = CHECKSUM_HEADERS[normalizedAlgo];
                
                if (headerName) {
                    headers[headerName] = checksum;
                } else {
                    console.warn(`⚠️ Could not map algorithm "${algorithm}" to checksum header name`);
                }
            }
            
            const options = {
                method: 'PUT',
                body: chunk,
                headers
            };

            if (signal) {
                options.signal = signal;
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                if (response.status === 403) {
                    let body = '';
                    try {
                        body = await response.text();
                    } catch (e) {
                        // ignore read errors
                    }
                    // Any 403 from S3 presigned URL is treated as URL expiry.
                    // Orchestrator has MAX_URL_REFRESHES safeguard to prevent infinite loops.
                    logger.warn(`Chunk upload got 403 — treating as URL expiry. Response: ${body.slice(0, 200)}`);
                    return rejectWithValue({ 
                        type: 'URL_EXPIRED', 
                        message: 'Presigned URL expired',
                        status: 403,
                        body: body.slice(0, 200)  // Keep body for debugging
                    });
                }
                throw new Error(`Chunk upload failed with status ${response.status}`);
                            }

            return {
                etag: response.headers.get('etag'),
                checksum: checksum
            };
        } catch (error) {
            if (error.name === 'AbortError') {
                return rejectWithValue({ type: 'ABORTED', message: 'Upload aborted' });
            }
            return rejectWithValue({ type: 'ERROR', message: error.message });
        }
    }
);

// Initial state
const initialState = {
    uploadedFiles: [],
    currentUpload: null,
    uploadProgress: {},
    isLoading: false,
    isUploading: false,
    error: null,
};

// Upload slice
const uploadSlice = createSlice({
    name: 'upload',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setUploadProgress: (state, action) => {
            const { uploadId, progress } = action.payload;
            state.uploadProgress[uploadId] = progress;
        },
        clearUploadProgress: (state, action) => {
            delete state.uploadProgress[action.payload];
        },
        setCurrentUpload: (state, action) => {
            state.currentUpload = action.payload;
        },
        clearCurrentUpload: (state) => {
            state.currentUpload = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Start Batch Upload
            .addCase(startBatchUpload.pending, (state) => {
                state.isUploading = true;
                state.error = null;
            })
            .addCase(startBatchUpload.fulfilled, (state, action) => {
                state.isUploading = false;
                state.currentUpload = action.payload;
            })
            .addCase(startBatchUpload.rejected, (state, action) => {
                state.isUploading = false;
                state.error = action.payload;
            })
            // Complete Upload
            .addCase(completeUpload.pending, (state) => {
                state.isUploading = true;
                state.error = null;
            })
            .addCase(completeUpload.fulfilled, (state, action) => {
                state.isUploading = false;
                state.currentUpload = null;
            })
            .addCase(completeUpload.rejected, (state, action) => {
                state.isUploading = false;
                state.error = action.payload;
            })
            // Batch Complete Upload
            .addCase(batchCompleteUpload.pending, (state) => {
                state.isUploading = true;
                state.error = null;
            })
            .addCase(batchCompleteUpload.fulfilled, (state, action) => {
                state.isUploading = false;
                state.currentUpload = null;
            })
            .addCase(batchCompleteUpload.rejected, (state, action) => {
                state.isUploading = false;
                state.error = action.payload;
            })
            .addCase(fetchUploadedFiles.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUploadedFiles.fulfilled, (state, action) => {
                state.isLoading = false;
                //  FIX: Extract Datasets array from response, fallback to empty array
                const payload = action.payload;
                state.uploadedFiles = Array.isArray(payload) 
                    ? payload 
                    : (payload?.Datasets || payload?.datasets || []);
            })
            .addCase(fetchUploadedFiles.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Upload Chunk
            .addCase(uploadChunk.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    setUploadProgress,
    clearUploadProgress,
    setCurrentUpload,
    clearCurrentUpload
} = uploadSlice.actions;

export default uploadSlice.reducer;
